import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { iDropdown } from ".";
import { MisModel } from "../../interfaces/ActionStartResponse";
import { FormatReportDateTime } from "../../Logic/DateTimeFormatters";
import { RootState } from "../../state/store";
import { getSessionValue, saveSessionValue } from "../../utilities/localStore/calls";
import { addItem, getItem } from "../../utilities/localStore/indexDBCalls";
import CheckBoxList from "../Generic/CheckboxList";
import ConfirmDialog from "../Generic/ConfirmDialog";
import FindDropdown from "../Generic/FindDropdown";
import { misReportFilter } from "./interfaces";
import { formatDatePeriods, validateDateRange } from "./logic";
import { GetDepartments, GetMisDownloadReportData, getMisModel, GetUsers } from "./tenantApiCalls";

type MailboxScope = { mailboxes: number[]; misModelIDs: number[] };

function MIS() {
    const [users, setUsers] = React.useState<iDropdown[]>([]);
    const [departments, setDepartments] = React.useState<iDropdown[]>([]);
    const [mailboxes, setMailboxes] = React.useState<iDropdown[]>([]);
    const [showFinishModal, setShowFinishModal] = React.useState<boolean>(false);
    const [showErorModal, setShowErrorModal] = React.useState<{ message: string[]; visible: boolean }>({
        message: [],
        visible: false,
    });
    const [misReportFilters, setMisReportFilters] = React.useState<misReportFilter[]>([]);
    const [misModelsState, setMisModelsState] = React.useState<MisModel | undefined>(undefined);
    const currentUser = useSelector((state: RootState) => state.UserInformation);
    const permissions = useSelector((state: RootState) => state.permissions);
    const inputDateStartRef = React.useRef<HTMLInputElement>(null);
    const inputDateEndRef = React.useRef<HTMLInputElement>(null);
    const misNameRef = React.useRef<HTMLInputElement>(null);
    let defaultMisReport: misReportFilter = {
        uId: 0,
        updateAt: new Date(),
        name: "",
        dateTo: FormatReportDateTime(new Date(), "23:59"),
        dateFrom: FormatReportDateTime(new Date(), "00:00"),
        mailboxes: [],
        users: [],
        serviceCentres: [],
        primaryfieldkey: { fieldKey: 0, fieldLabel: "" },
        filteredMisFieldKey: { fieldKey: 0, fieldLabel: "" },
        reportType: 2,
        includeAllEnquiriesIncludingOnesWithoutMISData: false,
        extractUnresolvedEnquiriesOnly: false,
        hideZeros: false,
        sortBy: 1,
        timePeriod: "Custom Period",
        misFields: [],
        enquiryFields: [
            "Date MIS Captured",
            "Mailbox Name",
            "Assigned User",
            "Enquirer Reference",
            "Enquirer Name",
            "Enquirer Email",
            "Enquirer Subject",
            "Received",
            "Completed",
            "Enquiry Status Now",
        ],
        includeFilterInExtractAsHeader: false,
        includePeriodinExtract: false,
        fileName: "",
        misModelIDs: [],
    };

    const reportType = [
        { label: "Summary Totals", value: 1 },
        { label: "All Enquiries", value: 2 },
        { label: "Detailed Enquiries", value: 4 },
    ];

    const hideZeros = [
        { label: "Hide Zero Values", value: true },
        { label: "Show Zero Values", value: false },
    ];

    const sortBy = [
        { label: "Sort By Description", value: 1 },
        { label: "Sort By Count", value: 2 },
    ];

    const enquiryColumns = [
        { label: "Date MIS Captured", value: "Date MIS Captured" },
        { label: "Mailbox Name", value: "Mailbox Name" },
        { label: "Assigned User", value: "Assigned User" },
        { label: "Enquirer Reference", value: "Enquirer Reference" },
        { label: "Enquirer Name", value: "Enquirer Name" },
        { label: "Enquirer Email", value: "Enquirer Email" },
        { label: "Enquirer Subject", value: "Enquirer Subject" },
        { label: "Received", value: "Received" },
        { label: "Completed", value: "Completed" },
        { label: "Enquiry Status Now", value: "Enquiry Status Now" },
        { label: "Number of Interactions", value: "Number of interactions" },
        { label: "Work Items", value: "Work Items" },
    ];
    const [misReportFilter, setmisReportFilter] = React.useState<misReportFilter>(defaultMisReport);
    useEffect(() => {
        (async () => {
            getSavedReportFilters();
        })();
    }, []);

    useEffect(() => {
        (async () => {
            var uData: iDropdown[] | null = getSessionValue("users");
            var dData: iDropdown[] | null = getSessionValue("departments");
            let allDepartments: {
                title: any;
                value: any;
            }[] = [];
            if (!uData) {
                let userData = await GetUsers();
                if (userData.isOk && userData.data) {
                    let allUsers = userData.data.users
                        .filter(c => c.blocked == false)
                        .map((x: any) => {
                            return { title: x.firstname + " " + x.lastname, value: x.id };
                        });
                    saveSessionValue(
                        "users",
                        allUsers.sort((a: any, b: any) => a.title.localeCompare(b.title)),
                    );
                    setUsers(allUsers.sort((a: any, b: any) => a.title.localeCompare(b.title)));
                }
            } else {
                setUsers(uData);
            }
            if (!dData) {
                var departmentData = await GetDepartments();
                if (departmentData.isOk && departmentData.data) {
                    let departmentsWithMailboxes = currentUser.mailboxes.flatMap(c => c.serviceCentreID);

                    allDepartments = departmentData.data.items
                        .filter(c => departmentsWithMailboxes.includes(c.id))
                        .map((x: any) => {
                            return { title: x.name, value: x.id };
                        });
                    saveSessionValue(
                        "departments",
                        allDepartments.sort((a: any, b: any) => a.title.localeCompare(b.title)),
                    );
                    setDepartments(allDepartments.sort((a: any, b: any) => a.title.localeCompare(b.title)));
                    setmisReportFilter(prevData => ({
                        ...prevData,
                        serviceCentres: allDepartments.flatMap(c => c.value),
                    }));
                }
            } else {
                setDepartments(dData);
                setmisReportFilter(prevData => ({
                    ...prevData,
                    serviceCentres: dData!.flatMap(c => c.value),
                }));
            }
            let allMailBoxesUserHasAccessTo = currentUser.mailboxes
                .map(x => {
                    return {
                        title: x.name,
                        value: x.id,
                    };
                })
                .sort((a, b) => a.title.localeCompare(b.title));
            let mailBoxesUserCanviewReport = permissions.Mailboxes.filter(x => x.Permissions.ViewReports);
            saveSessionValue(
                "mailboxes",
                allMailBoxesUserHasAccessTo.filter(x => mailBoxesUserCanviewReport.some(c => c.ID == x.value)),
            );
            setMailboxes(
                allMailBoxesUserHasAccessTo.filter(x => mailBoxesUserCanviewReport.some(c => c.ID == x.value)),
            );
            setmisReportFilter(prevData => ({
                ...prevData,
                mailboxes: allMailBoxesUserHasAccessTo.flatMap(c => c.value),
            }));
        })();
    }, []);
    const getSavedReportFilters = async () => {
        let savedFilters: misReportFilter[] = await getItem("ReportConfig", "all");
        setMisReportFilters(savedFilters);
    };
    const mailboxScopeFor = (serviceCentreIDs: number[]): MailboxScope => {
        const inScope = currentUser.mailboxes.filter(c => serviceCentreIDs.includes(c.serviceCentreID));
        return {
            mailboxes: inScope.map(x => x.id),
            misModelIDs: [...new Set(inScope.map(x => x.misModelID))].sort((a, b) => a - b),
        };
    };

    const toDefaultMisFields = (model: MisModel) =>
        model.fields
            .filter(c => c.label != "" && c.label != " " && !c.fieldType.startsWith("Display"))
            .map(str => ({
                fieldKey: parseInt(str.id.toString()),
                fieldLabel: str.label,
            }));

    // scope: the mailboxes and MIS models in play for "All Mailboxes" (0). Callers that have just changed the
    // department pass it in, because misReportFilter in this closure still holds the previous department's values.
    const getMISModelForMailbox = async (
        mailboxID: number,
        scope: MailboxScope = mailboxScopeFor(misReportFilter.serviceCentres),
    ) => {
        if (mailboxID === undefined) {
            setMisModelsState(undefined);
            updateMailboxBasedOnServiceCentre(misReportFilter.serviceCentres);
            setmisReportFilter(prevData => ({
                ...prevData,
                misFields: [],
            }));
            return;
        }
        // "All Mailboxes" (0) has a model only when every mailbox in scope shares one (0 means no model). Otherwise
        // there is nothing to show: keep the column selection hidden and don't ask the API for mailbox 0, which it
        // always rejects.
        if (mailboxID === 0 && !(scope.misModelIDs.length === 1 && scope.misModelIDs[0])) {
            setMisModelsState(undefined);
            setmisReportFilter(prevData => ({
                ...prevData,
                misFields: [],
                mailboxes: scope.mailboxes,
            }));
            return;
        }
        const fetchMailboxID = mailboxID === 0 ? scope.mailboxes[0] : mailboxID;

        let existingModel: MisModel | undefined = undefined;
        let allModels = getSessionValue("misModels") as Array<MisModel>;
        if (allModels) {
            existingModel = allModels.find(x => x.key == fetchMailboxID);
        }
        let model: MisModel | undefined = existingModel;
        if (model == undefined) {
            const response = await getMisModel(fetchMailboxID);
            if (response.isOk && response.data) {
                model = response.data.misModel;
                model.key = fetchMailboxID;
                saveSessionValue("misModels", [...(allModels ?? []), model]);
            }
        }
        setMisModelsState(model);
        setmisReportFilter(prevData => ({
            ...prevData,
            misFields: model ? toDefaultMisFields(model) : [],
        }));

        updateReportFilters("mailboxes", [mailboxID]);
    };

    const updateReportFilters = <K extends keyof misReportFilter>(key: K, value: misReportFilter[K]) => {
        setmisReportFilter(prevData => ({
            ...prevData,
            [key]: value,
        }));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target == null || e.target.value == null) {
            return;
        }

        if (e.target.id == "dateFrom" && e.target != null) {
            setmisReportFilter(prevData => ({
                ...prevData,
                dateFrom: e.target.value,
                timePeriod: "Custom Period",
            }));
        } else if (e.target.id == "dateTo" && e.target != null) {
            setmisReportFilter(prevData => ({
                ...prevData,
                dateTo: e.target.value,
                timePeriod: "Custom Period",
            }));
        }
    };

    const changeTimePeriod = (period: string) => {
        let { startDate, endDate } = formatDatePeriods(period);
        setmisReportFilter(prevData => ({
            ...prevData,
            timePeriod: period,
            dateFrom: startDate,
            dateTo: endDate,
        }));
    };

    const generateReport = async () => {
        if (misReportFilter.mailboxes.length == 0) {
            misReportFilter.mailboxes = mailboxes.flatMap(x => x.value);
        }
        if (misReportFilter.mailboxes.length == 1 && misReportFilter.mailboxes[0] === 0) {
            let mailboxesToSelect = currentUser.mailboxes
                .filter(c => misReportFilter.serviceCentres.includes(c.serviceCentreID))
                .map(x => x.id);
            misReportFilter.mailboxes = mailboxesToSelect;
        }
        if (misReportFilter.name && misReportFilter.name.trim() != "") {
            misReportFilter.updateAt = new Date();

            let maxId = Math.max(...misReportFilters.map(x => x.uId));
            if (maxId == -Infinity) {
                maxId = 0;
            }
            if (isNaN(maxId)) {
                maxId = 1;
            } else {
                maxId = misReportFilter.uId == 0 ? maxId + 1 : misReportFilter.uId;
            }
            misReportFilter.uId = maxId;
            addItem("ReportConfig", misReportFilter);
        }
        let result = await GetMisDownloadReportData(misReportFilter);
        if (result.isOk && result.blob) {
            const url = window.URL.createObjectURL(result.blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                misReportFilter.fileName != ""
                    ? misReportFilter.fileName + ".xlsx"
                    : "MIS_" + reportType.find(x => x.value == misReportFilter.reportType)!.label + "_Report.xlsx",
            );
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
        } else {
            if (result.validationErrors?.errors) {
                let allErrors = Object.values(result.validationErrors.errors);
                alert(allErrors.flat().join("\n"));
            } else {
                alert("An error occurred while generating the MIS report. error code: " + result.status);
            }
        }
        getSavedReportFilters();
        setShowFinishModal(false);
    };
    const showCompleteModal = () => {
        let message: string[] = [];
        message.push(...validateDateRange(12, new Date(misReportFilter.dateFrom), new Date(misReportFilter.dateTo)));
        if (misReportFilter.reportType == 2) {
            if (misReportFilter.enquiryFields.length == 0) {
                message.push("At least one enquiry column must be selected for 'All Enquiries' report type.");
            }
            if (
                isNaN(Date.parse(misReportFilter.dateTo.toString())) ||
                isNaN(Date.parse(misReportFilter.dateFrom.toString()))
            ) {
                message.push("Please enter valid date and time for the report period.");
            }
        }
        if (message.length > 0) {
            setShowErrorModal({ message: message, visible: true });
        } else {
            setShowFinishModal(true);
        }
    };

    const updateMISSavedData = (val: number) => {
        const selectedSavedFilter = misReportFilters.find(x => x.uId == val);
        if (selectedSavedFilter) {
            misNameRef.current!.value = selectedSavedFilter.name;
            setmisReportFilter(prevData => ({
                ...prevData,
                name: selectedSavedFilter.name,
                uId: selectedSavedFilter.uId,
            }));
        } else {
            misNameRef.current!.value = "";
            setmisReportFilter(prevData => ({
                ...prevData,
                name: "",
                uId: 0,
            }));
        }
    };

    const updateMailboxBasedOnServiceCentre = (serviceCentreIDs: number[]) => {
        const scope = mailboxScopeFor(serviceCentreIDs);
        updateReportFilters("mailboxes", scope.mailboxes);
        updateReportFilters("misModelIDs", scope.misModelIDs);
        return scope;
    };

    return (
        <div className="alternateView">
            <div
                className="col1"
                style={{ width: "210px" }}
            >
                <div className="header">MIS Report Extracts</div>
                <div style={{ padding: "5px", clear: "both", paddingLeft: "10px" }}>
                    <b>Previous Report Filters</b>
                    <div
                        style={{
                            overflowY: "auto",
                            width: "100%",
                            height: "100%",
                            maxHeight: "700px",
                        }}
                    >
                        <ul className="misReportList">
                            {misReportFilters.map(filter => (
                                <li
                                    key={filter.uId}
                                    onClick={() => {
                                        setmisReportFilter(filter);
                                        getMISModelForMailbox(
                                            filter.mailboxes.length == 1 ? filter.mailboxes[0] : 0,
                                            mailboxScopeFor(filter.serviceCentres),
                                        );
                                    }}
                                >
                                    {filter.name}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
            <div
                className="col2"
                style={{ padding: "0px 0px 0px 15px" }}
            >
                <div
                    style={{
                        textAlign: "right",
                        position: "sticky",
                        top: "00px",
                        background: "white",
                        width: "100%",
                        zIndex: 16874,
                        padding: "10px",
                    }}
                >
                    <button
                        className="g-btn g-btn-action"
                        onClick={showCompleteModal}
                    >
                        Generate Report
                    </button>
                </div>
                {/**  <div className="subHeaderWithLine">
                    <div className="sub-col-1">Load:&nbsp; &nbsp; </div>
                    <div className="sub-col-2">
                        <hr className="noPadding" />
                    </div>
                </div>
                Load a previous data extract configuration.
                <br />
                <div style={{ border: "solid 1px #ccc", overflowY: "auto", width: "100%", height: "75px" }}>
                    <div style={{ padding: "7px" }}> sds</div>
                </div>
                <br />
                <br /> */}
                <div
                    className="subHeaderWithLine"
                    style={{ marginTop: "10px" }}
                >
                    <div className="sub-col-1">Filter:&nbsp; &nbsp; </div>
                    <div className="sub-col-2">
                        <hr className="noPadding" />
                    </div>
                </div>
                Select the department, mailbox, or user to extract on:
                <div className="misRow">
                    <div className="misRow-col1">Departments:</div>
                    <div className="misRow-col2">
                        {" "}
                        <FindDropdown
                            id={222}
                            label={
                                misReportFilter.serviceCentres.length == 1
                                    ? departments.find(x => x.value == misReportFilter.serviceCentres[0])!.title
                                    : "All Departments"
                            }
                            styles={{ float: "left", background: "#fff", border: "solid 1px #CFCFCF" }}
                        >
                            {[
                                <li
                                    key={"All_Departms"}
                                    title={"All Departments"}
                                >
                                    <button
                                        onClick={() => {
                                            updateReportFilters(
                                                "serviceCentres",
                                                departments.flatMap(x => x.value),
                                            );
                                            const scope = updateMailboxBasedOnServiceCentre(
                                                departments.flatMap(x => x.value),
                                            );
                                            getMISModelForMailbox(0, scope);
                                        }}
                                    >
                                        {"All Departments"}
                                    </button>
                                </li>,
                                ...departments.map(x => {
                                    return (
                                        <li
                                            key={x.title}
                                            title={x.title}
                                        >
                                            <button
                                                onClick={() => {
                                                    updateReportFilters("serviceCentres", [x.value]);
                                                    const scope = updateMailboxBasedOnServiceCentre([x.value]);
                                                    getMISModelForMailbox(0, scope);
                                                }}
                                            >
                                                {x.title}
                                            </button>
                                        </li>
                                    );
                                }),
                            ]}
                        </FindDropdown>
                    </div>
                </div>
                <div className="misRow">
                    <div className="misRow-col1">Mailbox:</div>
                    <div className="misRow-col2">
                        {" "}
                        <FindDropdown
                            id={231}
                            label={
                                misReportFilter.mailboxes.length == 1 && misReportFilter.mailboxes[0] !== 0
                                    ? mailboxes.find(x => x.value == misReportFilter.mailboxes[0])!.title
                                    : "All Mailboxes"
                            }
                            styles={{ float: "left", background: "#fff", border: "solid 1px #CFCFCF" }}
                        >
                            {[
                                <li
                                    key={"All_Mailboxms"}
                                    title={"All Mailboxes"}
                                >
                                    <button
                                        onClick={() => {
                                            getMISModelForMailbox(0);
                                        }}
                                    >
                                        {"All Mailboxes"}
                                    </button>
                                </li>,

                                ...mailboxes

                                    .filter(m =>
                                        currentUser.mailboxes
                                            .filter(c => c.id == m.value)
                                            .some(c =>
                                                misReportFilter.serviceCentres
                                                    .flatMap(d => d)
                                                    .includes(c.serviceCentreID),
                                            ),
                                    )
                                    .map(x => {
                                        return (
                                            <li
                                                key={x.title}
                                                title={x.title}
                                            >
                                                <button
                                                    onClick={() => {
                                                        getMISModelForMailbox(x.value);
                                                    }}
                                                >
                                                    {x.title}
                                                </button>
                                            </li>
                                        );
                                    }),
                            ]}
                        </FindDropdown>
                    </div>
                </div>
                <div className="misRow">
                    <div className="misRow-col1">Users:</div>
                    <div className="misRow-col2">
                        {" "}
                        <FindDropdown
                            id={2322}
                            label={
                                misReportFilter.users.length == 1
                                    ? users.find(x => x.value == misReportFilter.users[0])!.title
                                    : "All Users"
                            }
                            styles={{ float: "left", background: "#fff", border: "solid 1px #CFCFCF" }}
                        >
                            {[
                                <li
                                    key={"All_Usersms"}
                                    title={"All Users"}
                                >
                                    <button
                                        onClick={() => {
                                            updateReportFilters(
                                                "users",
                                                users.flatMap(x => x.value),
                                            );
                                        }}
                                    >
                                        {"All Users"}
                                    </button>
                                </li>,
                                ...users.map(x => {
                                    return (
                                        <li
                                            key={x.title}
                                            title={x.title}
                                        >
                                            <button
                                                onClick={() => {
                                                    updateReportFilters("users", [x.value]);
                                                }}
                                            >
                                                {x.title}
                                            </button>
                                        </li>
                                    );
                                }),
                            ]}
                        </FindDropdown>
                    </div>
                </div>
                <div className="misRow">
                    <div className="misRow-col1">Include "Filter" in the extract as a header</div>
                    <div className="misRow-col2">
                        <input
                            type="checkbox"
                            className="g-Checkbox-default"
                            style={{ marginTop: "3px" }}
                            checked={misReportFilter.includeFilterInExtractAsHeader}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                updateReportFilters("includeFilterInExtractAsHeader", e.target.checked);
                            }}
                            id="includeFilterInExtractAsHeader"
                        />
                    </div>
                </div>
                <br />
                <br />
                <div className="subHeaderWithLine">
                    <div className="sub-col-1">Style:&nbsp; &nbsp; </div>
                    <div className="sub-col-2">
                        <hr className="noPadding" />
                    </div>
                </div>
                Select the extract detail, zero setting and sort options:
                <div className="misRow">
                    <div className="misRow-col1">Detail:</div>
                    <div className="misRow-col2">
                        {" "}
                        <FindDropdown
                            id={222}
                            label={reportType.find(x => x.value == misReportFilter.reportType)!.label}
                            styles={{ float: "left", background: "#fff", border: "solid 1px #CFCFCF" }}
                        >
                            {[
                                <li
                                    key={"Summary_Total"}
                                    title={"Summary Totals"}
                                >
                                    <button
                                        onClick={() => {
                                            updateReportFilters("reportType", 1);
                                        }}
                                    >
                                        {"Summary Totals"}
                                    </button>
                                </li>,
                                <li
                                    key={"All_Enquiries"}
                                    title={"All Enquiries"}
                                >
                                    <button
                                        onClick={() => {
                                            updateReportFilters("reportType", 2);
                                        }}
                                    >
                                        {"All Enquiries"}
                                    </button>
                                </li>,
                                <li
                                    key={"Detailed_Enquiries"}
                                    title={"Detailed Enquiries"}
                                >
                                    <button
                                        onClick={() => {
                                            updateReportFilters("reportType", 4);
                                        }}
                                    >
                                        {"Detailed Enquiries"}
                                    </button>
                                </li>,
                            ]}
                        </FindDropdown>
                    </div>
                </div>
                {misReportFilter.reportType == 1 && (
                    <div className="misRow">
                        <div className="misRow-col1">Hide Zeros:</div>
                        <div className="misRow-col2">
                            {" "}
                            <FindDropdown
                                id={222}
                                label={hideZeros.find(x => x.value == misReportFilter.hideZeros)!.label}
                                styles={{ float: "left", background: "#fff", border: "solid 1px #CFCFCF" }}
                            >
                                {[
                                    <li
                                        key={"Hide_Zero_Values"}
                                        title={"Hide Zero Values"}
                                    >
                                        <button
                                            onClick={() => {
                                                updateReportFilters("hideZeros", true);
                                            }}
                                        >
                                            {"Hide Zero Values"}
                                        </button>
                                    </li>,
                                    <li
                                        key={"Show_Zero_Values"}
                                        title={"Show Zero Values"}
                                    >
                                        <button
                                            onClick={() => {
                                                updateReportFilters("hideZeros", false);
                                            }}
                                        >
                                            {"Show Zero Values"}
                                        </button>
                                    </li>,
                                ]}
                            </FindDropdown>
                        </div>
                    </div>
                )}
                {misReportFilter.reportType == 1 && (
                    <div className="misRow">
                        <div className="misRow-col1">Sort By:</div>
                        <div className="misRow-col2">
                            {" "}
                            <FindDropdown
                                id={222}
                                label={sortBy.find(x => x.value == misReportFilter.sortBy)!.label}
                                styles={{ float: "left", background: "#fff", border: "solid 1px #CFCFCF" }}
                            >
                                {[
                                    <li
                                        key={"Sort_By_Descriptionl"}
                                        title={"Sort By Description"}
                                    >
                                        <button
                                            onClick={() => {
                                                updateReportFilters("sortBy", 1);
                                            }}
                                        >
                                            {"Sort By Description"}
                                        </button>
                                    </li>,
                                    <li
                                        key={"Sort_By_Count"}
                                        title={"Sort By Count"}
                                    >
                                        <button
                                            onClick={() => {
                                                updateReportFilters("sortBy", 2);
                                            }}
                                        >
                                            {"Sort By Count"}
                                        </button>
                                    </li>,
                                ]}
                            </FindDropdown>
                        </div>
                    </div>
                )}
                <br />
                <br />
                {misReportFilter.reportType == 1 && (
                    <>
                        {" "}
                        <div className="subHeaderWithLine">
                            <div className="sub-col-1">Grouping:&nbsp; &nbsp; </div>
                            <div className="sub-col-2">
                                <hr className="noPadding" />
                            </div>
                        </div>
                        Select the extract grouping options:
                        <div className="misRow">
                            <div className="misRow-col1">Primary:</div>
                            <div className="misRow-col2">
                                {" "}
                                <FindDropdown
                                    id={2243}
                                    label={
                                        misModelsState == undefined
                                            ? "none"
                                            : misModelsState.fields.length > 0
                                              ? misModelsState.fields.find(
                                                    x => x.id == misReportFilter.primaryfieldkey.fieldKey,
                                                )?.label || "<none>"
                                              : "none"
                                    }
                                    styles={{ float: "left", background: "#fff", border: "solid 1px #CFCFCF" }}
                                >
                                    {[
                                        <li
                                            key={"none"}
                                            title={"<none>"}
                                        >
                                            <button
                                                onClick={() => {
                                                    updateReportFilters("primaryfieldkey", {
                                                        fieldKey: 0,
                                                        fieldLabel: "",
                                                    });
                                                }}
                                            >
                                                {"<none>"}
                                            </button>
                                        </li>,

                                        ...(misModelsState
                                            ? misModelsState.fields
                                                  .filter(c => c.label != "" && c.label != " ")
                                                  .filter(x => x.fieldType == "Dropdown")
                                                  .map(x => {
                                                      return (
                                                          <li
                                                              key={x.label}
                                                              title={x.label + " (" + x.fieldType + ")"}
                                                          >
                                                              <button
                                                                  onClick={() => {
                                                                      updateReportFilters("primaryfieldkey", {
                                                                          fieldKey: x.id,
                                                                          fieldLabel: x.label,
                                                                      });
                                                                  }}
                                                              >
                                                                  {x.label + " (" + x.fieldType + ")"}
                                                              </button>
                                                          </li>
                                                      );
                                                  })
                                            : []),
                                    ]}
                                </FindDropdown>
                                <br />
                                <br /> The primary column forms the rows in the extract report.
                            </div>
                        </div>
                        <div className="misRow">
                            <div className="misRow-col1">Select the MIS model Column:</div>
                            <div className="misRow-col2">
                                {" "}
                                <FindDropdown
                                    id={2243}
                                    label={
                                        misModelsState == undefined
                                            ? "none"
                                            : misModelsState.fields.length > 0
                                              ? misModelsState.fields.find(
                                                    x => x.id == misReportFilter.filteredMisFieldKey.fieldKey,
                                                )?.label || "<none>"
                                              : "none"
                                    }
                                    styles={{ float: "left", background: "#fff", border: "solid 1px #CFCFCF" }}
                                >
                                    {[
                                        <li
                                            key={"none"}
                                            title={"<none>"}
                                        >
                                            <button
                                                onClick={() => {
                                                    updateReportFilters("filteredMisFieldKey", {
                                                        fieldKey: 0,
                                                        fieldLabel: "",
                                                    });
                                                }}
                                            >
                                                {"<none>"}
                                            </button>
                                        </li>,

                                        ...(misModelsState
                                            ? misModelsState.fields
                                                  .filter(c => c.label != "" && c.label != " ")
                                                  .filter(x => x.fieldType == "Dropdown")
                                                  .map(x => {
                                                      return (
                                                          <li
                                                              key={x.label}
                                                              title={x.label + " (" + x.fieldType + ")"}
                                                          >
                                                              <button
                                                                  onClick={() => {
                                                                      updateReportFilters("filteredMisFieldKey", {
                                                                          fieldKey: x.id,
                                                                          fieldLabel: x.label,
                                                                      });
                                                                  }}
                                                              >
                                                                  {x.label + " (" + x.fieldType + ")"}
                                                              </button>
                                                          </li>
                                                      );
                                                  })
                                            : []),
                                    ]}
                                </FindDropdown>
                            </div>
                        </div>
                        <br />
                        <br />
                    </>
                )}
                {misReportFilter.mailboxes.length == 1 && misReportFilter.reportType == 2 && (
                    <>
                        <div className="subHeaderWithLine">
                            <div className="sub-col-1">MIS Model Column Selection &nbsp; &nbsp; </div>
                            <div className="sub-col-2">
                                <hr className="noPadding" />
                            </div>
                        </div>
                        Select the MIS Model columns to include in the extract.
                        <br />
                        {misModelsState && (
                            <CheckBoxList
                                id={"Actions_Id"}
                                options={misModelsState!.fields
                                    .filter(c => c.label != "" && c.label != " " && !c.fieldType.startsWith("Display"))
                                    .map(x => {
                                        return { label: x.label, value: x.id.toString() };
                                    })}
                                value={misReportFilter.misFields.map(str => str.fieldKey.toString())}
                                errorMessage=""
                                onChange={(val: string[]) => {
                                    updateReportFilters(
                                        "misFields",
                                        val.map(str => ({
                                            fieldKey: parseInt(str.toString()),
                                            fieldLabel: misModelsState!.fields.find(x => x.id.toString() == str)!.label,
                                        })),
                                    );
                                }}
                            ></CheckBoxList>
                        )}
                        <br />
                        <br />
                    </>
                )}
                {misReportFilter.reportType == 2 && (
                    <>
                        <div className="subHeaderWithLine">
                            <div className="sub-col-1">Enquiry Columns &nbsp; &nbsp; </div>
                            <div className="sub-col-2">
                                <hr className="noPadding" />
                            </div>
                        </div>
                        Select the enquiry columns to include in the report.
                        <br />
                        {enquiryColumns && (
                            <CheckBoxList
                                id={"Enquiries_Id"}
                                options={enquiryColumns}
                                value={misReportFilter.enquiryFields.map(str => str.toString())}
                                errorMessage=""
                                onChange={(val: string[]) => {
                                    updateReportFilters("enquiryFields", val);
                                }}
                            ></CheckBoxList>
                        )}
                        <div className="misRow">
                            <div className="misRow-col1">Include all enquiries including one without MIS data</div>
                            <div className="misRow-col2">
                                <input
                                    type="checkbox"
                                    className="g-Checkbox-default"
                                    style={{ marginTop: "3px" }}
                                    checked={misReportFilter.includeAllEnquiriesIncludingOnesWithoutMISData}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        updateReportFilters(
                                            "includeAllEnquiriesIncludingOnesWithoutMISData",
                                            e.target.checked,
                                        );
                                    }}
                                    id="includeAllEnquiriesIncludingOnesWithoutMISData"
                                />
                            </div>
                        </div>
                        <div className="misRow">
                            <div className="misRow-col1">Extract Unresolved Enquiries Only</div>
                            <div className="misRow-col2">
                                <input
                                    type="checkbox"
                                    className="g-Checkbox-default"
                                    style={{ marginTop: "3px" }}
                                    checked={misReportFilter.extractUnresolvedEnquiriesOnly}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        updateReportFilters("extractUnresolvedEnquiriesOnly", e.target.checked);
                                    }}
                                    id="extractUnresolvedEnquiriesOnly"
                                />
                            </div>
                        </div>
                        <br />
                        <br />
                    </>
                )}
                <div className="subHeaderWithLine">
                    <div className="sub-col-1">Period:&nbsp; &nbsp; </div>
                    <div className="sub-col-2">
                        <hr className="noPadding" />
                    </div>
                </div>
                Select the date range to report on:
                <div className="misRow">
                    <div className="misRow-col2">
                        <FindDropdown
                            id={222}
                            label={misReportFilter.timePeriod}
                            styles={{ float: "left", background: "#fff", border: "solid 1px #CFCFCF" }}
                        >
                            {[
                                <li
                                    key={"Today"}
                                    title={"Today"}
                                >
                                    <button
                                        onClick={() => {
                                            changeTimePeriod("Today");
                                        }}
                                    >
                                        {"Today"}
                                    </button>
                                </li>,
                                <li
                                    key={"This Week"}
                                    title={"This Week"}
                                >
                                    <button
                                        onClick={() => {
                                            changeTimePeriod("This Week");
                                        }}
                                    >
                                        {"This Week"}
                                    </button>
                                </li>,
                                <li
                                    key={"This Month"}
                                    title={"This Month"}
                                >
                                    <button
                                        onClick={() => {
                                            changeTimePeriod("This Month");
                                        }}
                                    >
                                        {"This Month"}
                                    </button>
                                </li>,
                                <li
                                    key={"Previous Day"}
                                    title={"Previous Day"}
                                >
                                    <button
                                        onClick={() => {
                                            changeTimePeriod("Previous Day");
                                        }}
                                    >
                                        {"Previous Day"}
                                    </button>
                                </li>,
                                <li
                                    key={"Previous Month"}
                                    title={"Previous Month"}
                                >
                                    <button
                                        onClick={() => {
                                            changeTimePeriod("Previous Month");
                                        }}
                                    >
                                        {"Previous Month"}
                                    </button>
                                </li>,
                                <li
                                    key={"Previous_Week"}
                                    title={"Previous Week "}
                                >
                                    <button
                                        onClick={() => {
                                            changeTimePeriod("Previous Week");
                                        }}
                                    >
                                        {"Previous Week"}
                                    </button>
                                </li>,
                                <li
                                    key={"Custom"}
                                    title={"Custom Period"}
                                >
                                    <button
                                        onClick={() => {
                                            changeTimePeriod("Custom Period");
                                        }}
                                    >
                                        {"Custom Period"}
                                    </button>
                                </li>,
                            ]}
                        </FindDropdown>
                    </div>
                </div>
                <div className="misRow">
                    <div className="misRow-col1">From: </div>
                    <div className="misRow-col2">
                        <input
                            type="datetime-local"
                            max="9999-12-31T23:59"
                            min="1999-12-31T23:59"
                            className="g-TextBox-default"
                            value={misReportFilter.dateFrom.toString().replace("T", " ")}
                            ref={inputDateStartRef}
                            placeholder="Start Date"
                            onChange={handleChange}
                            id="dateFrom"
                        ></input>
                    </div>
                </div>
                <div className="misRow">
                    <div className="misRow-col1">To: </div>
                    <div className="misRow-col2">
                        <input
                            type="datetime-local"
                            max="9999-12-31T23:59"
                            min="1999-12-31T23:59"
                            className="g-TextBox-default"
                            value={misReportFilter.dateTo.toString().replace("T", " ")}
                            placeholder="End Date"
                            ref={inputDateEndRef}
                            onChange={handleChange}
                            id="dateTo"
                        ></input>
                    </div>
                </div>
                <div className="misRow">
                    <div className="misRow-col1">Include "Period" in the extract as a header</div>
                    <div className="misRow-col2">
                        <input
                            type="checkbox"
                            className="g-Checkbox-default"
                            style={{ marginTop: "3px" }}
                            checked={misReportFilter.includePeriodinExtract}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                updateReportFilters("includePeriodinExtract", e.target.checked);
                            }}
                            id="includePeriodInExtractAsHeader"
                        />
                    </div>
                </div>
                <br />
            </div>
            {showFinishModal && (
                <ConfirmDialog
                    title="Finish"
                    onConfirm={generateReport}
                    onCancel={() => {
                        setShowFinishModal(false);
                    }}
                >
                    <div style={{ width: "550px" }}>
                        <p>The data extract has enough information to generate the data extract.</p>
                        <p>
                            Save settings for future use? (leave blank to not save, or select a previous setting to
                            overwrite)
                        </p>
                        <div className="misRow">
                            <div className="misRow-col1">New Settings:</div>
                            <div className="misRow-col2">
                                <input
                                    type="text"
                                    className="g-TextBox-default"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        updateReportFilters("name", e.currentTarget.value);
                                    }}
                                    ref={misNameRef}
                                    value={misReportFilter.name}
                                ></input>
                            </div>
                        </div>
                        <div className="misRow">
                            <div className="misRow-col1">Saved Settings:</div>
                            <div className="misRow-col2">
                                <FindDropdown
                                    id={231}
                                    label={
                                        misReportFilters.length > 0 &&
                                        misReportFilters.find(x => x.uId == misReportFilter.uId)
                                            ? misReportFilters.find(x => x.uId == misReportFilter.uId)!.name
                                            : "None"
                                    }
                                    styles={{ float: "left", background: "#fff", border: "solid 1px #CFCFCF" }}
                                >
                                    {[
                                        <li
                                            key={"none"}
                                            title={"None"}
                                        >
                                            <button
                                                onClick={() => {
                                                    updateMISSavedData(0);
                                                }}
                                            >
                                                {"None"}
                                            </button>
                                        </li>,
                                        ...misReportFilters.map(x => {
                                            return (
                                                <li
                                                    key={x.name + x.uId}
                                                    title={x.name}
                                                >
                                                    <button
                                                        onClick={() => {
                                                            updateMISSavedData(x.uId);
                                                        }}
                                                    >
                                                        {x.name}
                                                    </button>
                                                </li>
                                            );
                                        }),
                                    ]}
                                </FindDropdown>
                            </div>
                        </div>
                        <div className="misRow">
                            <div className="misRow-col1">File Name:</div>
                            <div className="misRow-col2">
                                <input
                                    type="text"
                                    className="g-TextBox-default"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        updateReportFilters("fileName", e.currentTarget.value);
                                    }}
                                    value={misReportFilter.fileName}
                                ></input>
                            </div>
                        </div>
                    </div>
                </ConfirmDialog>
            )}
            {showErorModal.visible == true && (
                <ConfirmDialog
                    title="Error"
                    onConfirm={() => {
                        setShowErrorModal({ message: [], visible: false });
                    }}
                >
                    <ul style={{ width: "650px" }}>
                        {showErorModal.message.map((m, i) => (
                            <li key={i}>{m}</li>
                        ))}
                    </ul>
                </ConfirmDialog>
            )}
        </div>
    );
}

export default MIS;
